/* eslint-disable @typescript-eslint/no-explicit-any */

// トークンの有効期限切れ、キャッシュ強制クリアにより未認証状態になった場合はTOP画面へ戻る。
// サインアウト: 読み出し元にTOP画面への遷移を実装
// 認証チェックの結果未認証発覚: 読み出し元にTOP画面への遷移を実装
// パスワード更新時にトークン取得失敗: 読み出し元にTOP画面への遷移を実装
// S3アクセスの際にトークン取得または、トークンリフレッシュ失敗: トークン取得またはリフレッシュ失敗時にTOP画面への遷移を実装(Mixin)
// API-GWアクセスの際にトークン取得または、トークンリフレッシュ失敗
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import MiiaFwConfig from '@/configs/config'
import { config, CognitoIdentityCredentials } from 'aws-sdk' // 【後で削除】クレデンシャルの更新に必要(S3の更新に必要): トークン取得またはリフレッシュ失敗時にTOP画面への遷移を実装(Mixin)
import type { BodyOfSigninApi } from '../types/signin'
import type { AuthInfo, TokenCheckParams, TokenInfo } from '../types/cognito/auth'
import {
  RemoveSessionInfo,
  tempDataGetItem,
  tempDataRemoveItem,
  tempDataSetItem,
  webApiCall
} from './miiaFwUtils'
import type { ErrorMessage } from '../types/error'
import { useRouter } from 'vue-router'
import { useToken } from '../composables/useToken'
import { useCommonModalStore } from '@/stores/commonModalStore'
import type { SignupParams } from '../types/signup'
import type { UpdatePasswordParams } from '../types/updatePassword'
import type { SignoutParams } from '../types/signout'
import { getAuthSystemByUserId } from '@/utils/auth/user'
import { AUTH_SYSTEM } from '@/constants/auth/user'

// if (MiiaFwConfig.config.authorization.cognitoIdentityEndpoint) {
//   config.cognitoidentity = { endpoint: MiiaFwConfig.config.authorization.cognitoIdentityEndpoint }
// }

// /**
//  * @module components/miiafw/mixin/MiiaFwCognito
//  * @description 認証や、属性情報を取得する機能を集合させたライブラリ。
//  */

/** 本ライブラリのグローバル変数 */
export const getAuthApiSubdirDup = () => {
  const getAuthApiSubdir: string =
    typeof MiiaFwConfig.config.authorization.authSubDirDup === 'string'
      ? MiiaFwConfig.config.authorization.authSubDirDup
      : 'without_auth' // 標準認証用
  return getAuthApiSubdir
}

/** disaster-auth用のグローバル変数 */
export const getAuthApiSubdir = () => {
  const getDisasterAuthApiSubdir: string =
    typeof MiiaFwConfig.config.authorization.authSubDir === 'string'
      ? MiiaFwConfig.config.authorization.authSubDir
      : 'miiafw-auth' // 標準認証用
  return getDisasterAuthApiSubdir
}

// // Interfaces
// /** トークン情報格納用DTO */
// export interface TokenInfo {
//   /** アクセストークン */
//   access_token?: string
//   /** リフレッシュトークン */
//   refresh_token?: string
//   /** IDトークン */
//   id_token?: string
//   /** デバイスキー */
//   device_key?: string
//   /** Authトークン */
//   auth_token?: string
//   /** トークンシークレット */
//   token_secret?: string
//   /** IDプロバイダ */
//   provider: string
//   /** セッションID */
//   session_id?: string
// }

// /** サインインステータスを格納するオブジェクト */
// export interface SignInInfo {
//   /** 有効期限切れかどうか */
//   isExpired: boolean
//   /** 認証がアップデートされたかどうか */
//   isUpdated: boolean
//   /** ユーザーID */
//   userId: string
//   /** セッション情報 */
//   tokens: TokenInfo
// }

// /** サインアップメソッドの引数(params) */
// interface SignupParams {
//   /** ユーザID */
//   user_id: string
//   /** メールアドレス */
//   email?: string
//   /** 携帯電話番号 */
//   phone_number?: string
//   /** パスワード */
//   password: string
//   /** システムID */
//   system_id: string
// }

// /** パスワード更新メソッドの引数(params) */
// interface UpdatePasswordParams {
//   /** 現在のパスワード */
//   current_password: string
//   /** 更新後のパスワード */
//   new_password: string
//   /** アクセストークン */
//   access_token?: string
// }

// /** サインアウトメソッドの引数(params) */
// interface SignoutParams {
//   /** アクセストークン */
//   access_token?: string
// }

// /**
//  * Vuexにストアされている'miiaFwStore/updateAuthInfo'を更新する。
//  * ちなみにmiiaFwStore/updateAuthInfoは現在使用されていない。
//  * @param isAutAuthInfo 'miiaFwStore/updateAuthInfo'の値を変更する。
//  */
// export const updateAuthInfo = (isAuthorized: boolean): void => {
//   const miiaFwStore = useMiiaFwStore()
//   miiaFwStore.updateAuthInfo(isAuthorized)
// }

/**
 * Vuexにストアされている'miiaFwStore/updateIsAuthorized'を更新する。
 * 認証が新しく実行されて、成功した際にtrueにする。
 * @param isAuthorized 認証が更新に成功したかどうか。
 */
export const updateIsAuthorized = (isAuthorized: boolean): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateIsAuthorized(isAuthorized)
}

/**
 * Vuexにストアされている属性情報('miiaFwStore/updateAttributes')、
 * センシティブ情報('miiaFwStore/updateSensitiveInfo')、
 * 認証情報('miiaFwStore/updateIsAuthorized')をクリーンアップする。
 */
export const cleanupAuthInfoInStore = (): void => {
  const miiaFwStore = useMiiaFwStore()
  miiaFwStore.updateAttributes(undefined) // cognitoAuth.attributesの初期値はundefined
  miiaFwStore.updateSensitiveInfo(undefined) // cognitoAuth.sensitiveInfoの初期値はundefined
  miiaFwStore.updateIsAuthorized(false)
}

/**
 * Cognitoのサインアップ処理を行う。
 * @param userName ユーザーID
 * @param email メールアドレス
 * @param password パスワード
 * @returns If resolved then 'サインアップに成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const signup = async (
  userName: string,
  email: string,
  phoneNumber: string,
  password: string
): Promise<string | ErrorMessage> => {
  const signupParams: SignupParams = {
    user_id: userName,
    password: password,
    system_id: MiiaFwConfig.config.sysId
  }
  if (email.length > 0) {
    signupParams.email = email
  }
  if (phoneNumber.length > 0) {
    signupParams.phone_number = phoneNumber
  }
  const args = { function: 'sign_up', params: signupParams }
  const url: string = MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/**
 * Cognitoのサインアップしたユーザーを認証コードを用いてアクティベートする。
 * @param userName ユーザー
 * @param verificationCode 認証コード
 * @returns If resolved then 'アクティベートに成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const activate = async (
  userName: string,
  verificationCode: string
): Promise<string | ErrorMessage> => {
  const args = {
    function: 'activate',
    params: {
      user_id: userName,
      verification_code: verificationCode,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  const url: string = MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

// /** 認証後に払い出される情報を格納したオブジェクト */
// export interface AuthInfo {
//   /** 有効期限切れかどうか */
//   isExpired: boolean
//   /** 認証がアップデートされたかどうか */
//   isUpdated: boolean
//   /**  ユーザーID */
//   userId: string
//   /** セッション情報` */
//   tokens: TokenInfo
//   /** 認証の種類 */
//   challengeName: string
//   /** セッション */
//   session: string
//   /** MFA認証が必要かどうか */
//   needMfa: boolean
// }

// interface BodyOfSigninApi {
//   function: string
//   params: {
//     user_id: string | null
//     password?: string | null
//     system_id: string
//     challenge_name?: string | null
//     session?: string | null
//     mfa_code?: string | null
//     expiration_date_n?: number
//   }
// }

/**
 * Cognitoのサインイン処理を行う。
 * サインインに成功した場合はセッションストレージの認証情報を更新してから、認証情報を返す。
 * @param userName ユーザーID
 * @param password パスワード
 * @returns If resolved then
 * {'isExpired': (boolean)有効期限切れかどうか,
 * 'isUpdated': (boolean->true) 認証がアップデートされたかどうか,
 * 'userId': (string)ユーザーID,
 * 'tokens': {'provider': (string->'cognito')認証を提供するプロバイダ,
 * 'access_token': (string)アクセストークン,
 * 'refresh_token': (string)リフレッシュトークン,
 * 'id_token': (string)IDトークン,
 * 'session_id': (string)セッションID,
 * 'device_key': (string)デバイスキー }}.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const signin = async (
  userName: string | null,
  password: string | null
): Promise<AuthInfo | ErrorMessage> => {
  const args: BodyOfSigninApi = {
    function: 'sign_in',
    params: {
      user_id: userName,
      password: password,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  if (MiiaFwConfig.config.authorization.expirationTime < Number.MAX_VALUE) {
    args.params.expiration_date_n = MiiaFwConfig.config.authorization.expirationTime
  }
  const url =
    userName && getAuthSystemByUserId(userName) === AUTH_SYSTEM.DUP
      ? MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
      : MiiaFwConfig.config.authEndpoint.concat('/', getAuthApiSubdir())
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          if (Object.prototype.hasOwnProperty.call(data.result, 'tokens')) {
            const { setTokens } = useToken()
            data.result.tokens.provider = 'cognito'
            setTokens(data.result.tokens)
            updateIsAuthorized(true)
            setUserIdToStorage(userName as string)
            setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
            updateCredentials(data.result.tokens, userName as string) // S3でAWS SDKを使用するために必要
            resolve(data.result)
          } else if (
            Object.prototype.hasOwnProperty.call(data.result, 'challengeName') &&
            Object.prototype.hasOwnProperty.call(data.result, 'session')
          ) {
            resolve(data.result)
          } else {
            reject({ message: 'sign in APIが想定外の実行結果を返しました。' })
          }
        }
      })
      .catch(error => {
        console.error(error)
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        // procAtSignout()は失敗しない。
        void procAtSignout().then(() => {
          reject({ message: error.message })
        })
      })
  })
}

// /**
//  * CognitoのMFA認証処理を行う。
//  * MFA認証に成功した場合はセッションストレージの認証情報を更新してから、認証情報を返す。
//  * @param challengeName 認証の種類
//  * @param session サインイン時に発行されたセッション情報
//  * @param userName ユーザーID
//  * @param mfaCode MFA検証コード
//  * @returns If resolved then
//  * {'isExpired': (boolean)有効期限切れかどうか,
//  * 'isUpdated': (boolean->true) 認証がアップデートされたかどうか,
//  * 'userId': (string)ユーザーID,
//  * 'tokens': {'provider': (string->'cognito')認証を提供するプロバイダ,
//  * 'access_token': (string)アクセストークン,
//  * 'refresh_token': (string)リフレッシュトークン,
//  * 'id_token': (string)IDトークン,
//  * 'session_id': (string)セッションID,
//  * 'device_key': (string)デバイスキー }}.
//  * If rejected then {'message': '[エラーメッセージ]'}.
//  */
export const authChallenge = async (
  challengeName: string | null,
  session: string | null,
  userName: string | null,
  mfaCode: string | null
): Promise<AuthInfo | ErrorMessage> => {
  const args: BodyOfSigninApi = {
    function: 'auth_challenge',
    params: {
      challenge_name: challengeName,
      session: session,
      user_id: userName,
      mfa_code: mfaCode,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  if (MiiaFwConfig.config.authorization.expirationTime < Number.MAX_VALUE) {
    args.params.expiration_date_n = MiiaFwConfig.config.authorization.expirationTime
  }
  const url: string =
    userName && getAuthSystemByUserId(userName) === AUTH_SYSTEM.DUP
      ? MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
      : MiiaFwConfig.config.authEndpoint.concat('/', getAuthApiSubdir())

  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          if (Object.prototype.hasOwnProperty.call(data.result, 'tokens')) {
            const { setTokens } = useToken()
            setTokens(data.result.tokens)
            updateIsAuthorized(true)
            setUserIdToStorage(userName as string)
            setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
            updateCredentials(data.result.tokens, userName as string) // 【後で削除】S3でAWS SDKを使用するために必要
            resolve(data.result)
          } else {
            reject({
              message: 'auth challenge APIが想定外の実行結果を返しました。'
            })
          }
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        // procAtSignout()は失敗しない。
        void procAtSignout().then(() => {
          reject({ message: error.message })
        })
      })
  })
}

/**
 * サインアウト処理を行う（任意の認証プロバイダで動作）。
 * @returns サインアウト処理に成功した際のメッセージをstringで返す。サインアウト処理でエラーが起きた場合はメッセージをオブジェクト{'message': '[エラーメッセージ]'}で返す。必ずresolvedになる。
 */
export const signout = async (): Promise<string | object> => {
  return new Promise(resolve => {
    // procAtSignout()は失敗しない。
    void procAtSignout().then(result => {
      resolve(result)
    })
  })
}

/**
 * Cognitoのユーザーのパスワードをリセットする。
 * @param userName ユーザーID
 * @returns If resolved then 'パスワードリセット成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const resetPassword = async (userName: string): Promise<string | ErrorMessage> => {
  const args = {
    function: 'reset_password',
    params: {
      user_id: userName,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  const url: string = MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/**
 * Cognitoのユーザーのパスワードを初期化する（認証コードによる認証が必要）。
 * @param userName ユーザーID
 * @param verificationCode 認証コード
 * @param password パスワード初期化に用いるパスワード
 * @returns If resolved then 'パスワード初期化に成功しました。'.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const initializePassword = async (
  userName: string,
  verificationCode: string,
  password: string
): Promise<string | ErrorMessage> => {
  const args = {
    function: 'initialize_password',
    params: {
      verification_code: verificationCode,
      user_id: userName,
      password: password,
      system_id: MiiaFwConfig.config.sysId
    }
  }
  const url: string = MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
  return new Promise((resolve, reject) => {
    webApiCall(url, 'POST', {}, args, 'json')
      .then(data => {
        if (typeof data === 'string') {
          // IE対応
          data = JSON.parse(data)
        }
        if (Object.prototype.hasOwnProperty.call(data, 'error')) {
          // ハンドリングされたエラー処理
          reject({ message: data.error })
        } else {
          resolve(data.result)
        }
      })
      .catch(error => {
        if (typeof error === 'string') {
          // IE対応
          error = JSON.parse(error)
        }
        reject({ message: error.message })
      })
  })
}

/**
 * Cognitoのユーザーのパスワードを変更する。
 * @param currentPassword 現在のパスワード
 * @param newPassword 新しいパスワード
 * @returns If resolved then 'パスワード更新に成功しました。'
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const updatePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<string | ErrorMessage> => {
  const { getTokens } = useToken()
  const commonModalStore = useCommonModalStore()

  const tokens = getTokens()
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      reject({ message: '未認証のユーザのパスワード更新が要求されました。' })
      void procAtSignout().then(async () => {
        await commonModalStore.openAlertModal({
          body: '未認証のユーザのパスワード更新が要求されました。'
        })
        const router = useRouter()
        if (router.currentRoute.value.path !== '/') void router.push({ path: '/' }) // 同じページに遷移させるとNavigation Errorになる。
      })
    } else {
      const upParams: UpdatePasswordParams = {
        current_password: currentPassword,
        new_password: newPassword
      }
      if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
        upParams.access_token = tokens.access_token
      }
      const args = {
        function: 'update_password',
        params: upParams
      }
      const url: string = MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
      webApiCall(
        url,
        'POST',
        MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
          ? { 'X-miiafw-token': tokens.access_token }
          : {},
        args,
        'json'
      )
        .then(data => {
          if (typeof data === 'string') {
            // IE対応
            data = JSON.parse(data)
          }
          if (Object.prototype.hasOwnProperty.call(data, 'error')) {
            // ハンドリングされたエラー処理
            reject({ message: data.error })
          } else {
            resolve(data.result)
          }
        })
        .catch(error => {
          if (typeof error === 'string') {
            // IE対応
            error = JSON.parse(error)
          }
          reject({ message: error.message })
        })
    }
  })
}

/**
 * signoutなどが呼び出す、サインアウト処理を行う本体部分。
 * セッション情報をクリアする。
 * @returns サインアウト処理に成功した際のメッセージをstringで返す。サインアウト処理でAPIの呼び出しにエラーが起きた場合はメッセージをオブジェクト{'message': '[エラーメッセージ]'}で返す。必ずresolvedになる。
 */
export const procAtSignout = async (): Promise<string | object> => {
  const { getTokens } = useToken()
  const tokens = getTokens()
  const userId: string | null = tempDataGetItem('user_id')
  clearTokens()
  cleanupAuthInfoInStore()
  // S3でAWS SDKを使用するために必要
  // サインアウト実行後に必ずキャッシュクリア(ユーザを変えてサインインしたときにエラーとなる。)
  // TODO: configの型問題解消
  if (config.credentials != null && (config as any).credentials.clearCachedId != null) {
    ;(config as any).credentials.clearCachedId()
  }
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      resolve('サインアウト不要です。')
    } else {
      if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'cognito'
      ) {
        const soParams: SignoutParams = { system_id: MiiaFwConfig.config.sysId }
        if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
          soParams.access_token = tokens.access_token
        }
        const args = { function: 'sign_out', params: soParams }

        const url: string =
          userId && getAuthSystemByUserId(userId) === AUTH_SYSTEM.DUP
            ? MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
            : MiiaFwConfig.config.authEndpoint.concat('/', getAuthApiSubdir())
        webApiCall(
          url,
          'POST',
          MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
            ? { 'X-miiafw-token': tokens.access_token }
            : {},
          args,
          'json'
        )
          .then(data => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              resolve(data.error)
            } else {
              resolve('サインアウトが成功しました。')
            }
          })
          .catch(error => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            resolve({ message: error.message })
          })
      } else {
        reject({ message: '対応していないIDプロバイダが設定されました。' })
      }
    }
  })
}

/**
 * 認証されているかどうかをチェックする。
 * 認証されていればIDプロバイダを返し、認証されていなかったりエラーが起こったりしたときにはサインアウト処理を行いエラーメッセージを返す。
 * @returns If resolved then {provider: (str)IDプロバイダ, isRefreshed: (boolean)トークンをリフレッシュしたか}.
 * If rejected then {'message': '[エラーメッセージ]'}.
 */
export const isAuthenticated = async (): Promise<object | ErrorMessage> => {
  const { getTokens, refreshToken } = useToken()
  const tokens = getTokens()
  return new Promise((resolve, reject) => {
    if (tokens === null) {
      // procAtSignout()は失敗しない。
      void procAtSignout().then(() => {
        reject({ message: 'サインアウト中です。' })
      })
    } else {
      if (
        Object.prototype.hasOwnProperty.call(tokens, 'provider') &&
        tokens.provider.toLowerCase() === 'cognito'
      ) {
        const tcParams: TokenCheckParams = {}
        if (MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'iam') {
          tcParams.access_token = tokens.access_token
        }
        const args = { function: 'token_check', params: tcParams }
        const userId: string | null = tempDataGetItem('user_id')
        const url: string =
          userId && getAuthSystemByUserId(userId) === AUTH_SYSTEM.DUP
            ? MiiaFwConfig.config.authEndpointDup.concat('/', getAuthApiSubdirDup())
            : MiiaFwConfig.config.authEndpoint.concat('/', getAuthApiSubdir())
        webApiCall(
          url,
          'POST',
          MiiaFwConfig.config.authorization.method?.toLocaleLowerCase() === 'custom_authorizer'
            ? { 'X-miiafw-token': tokens.access_token }
            : {},
          args,
          'json'
        )
          .then(data => {
            if (typeof data === 'string') {
              // IE対応
              data = JSON.parse(data)
            }
            if (Object.prototype.hasOwnProperty.call(data, 'error')) {
              // ハンドリングされたエラー処理
              // procAtSignout()は失敗しない。
              void procAtSignout().then(() => {
                reject({ message: data.error })
              })
            } else {
              if (data.result.isValid && data.result.userId) {
                // setTokens(tokens) // トークンはリフレッシュ後でもSession StrageやCookieに残るので不要
                // setUserIdToStorage(data.result.userId) // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
                updateCredentials(tokens, data.result.userId) // リフレッシュ後に揮発するので要設定後で削除】S3でAWS SDKを使用するために必要
                updateIsAuthorized(true) // リフレッシュ後に揮発するので要設定
                resolve({ provider: tokens.provider, isRefreshed: false })
              } else {
                refreshToken()
                  .then(() => {
                    resolve({ provider: tokens.provider, isRefreshed: true })
                  })
                  .catch(refreshError => {
                    // procAtSignout()は失敗しない。
                    void procAtSignout().then(() => {
                      reject({ message: refreshError.message })
                    })
                  })
              }
            }
          })
          .catch(error => {
            if (typeof error === 'string') {
              // IE対応
              error = JSON.parse(error)
            }
            // procAtSignout()は失敗しない。
            void procAtSignout().then(() => {
              reject({ message: error.message })
            })
          })
      }
    }
  })
}

// /**
//  * 再認証およびトークンをリフレッシュする。
//  * @param tokens getTokensで取得できるセッション情報
//  * @returns If resolved then {'access_token': (string)アクセストークン, 'refresh_token': (string)リフレッシュトークン, 'id_token': (string)IDトークン, 'device_key': (string)デバイスキー, 'auth_token': Authトークン, 'token_secret': (string)トークンシークレット, 'provider': (string)IDプロバイダ}. セッション情報を返す。
//  * If rejected then {'message': '[エラーメッセージ]'}.
//  */
// export const recertification = async (tokens: TokenInfo): Promise<TokenInfo | string | ErrorMessage> => {
//   return new Promise((resolve, reject) => {
//     if (Object.prototype.hasOwnProperty.call(tokens, 'provider') && tokens.provider.toLowerCase() === 'cognito') {
//       promptDialog(
//         'パスワードを入力してください。',
//         'password',
//         (password: string | null) => {
//           const userId = getUserIdToStorage()
//           signin(userId, password)
//             .then(() => {
//               // 有効期限切れ対応はしない。(サインイン時のみ)
//               refreshTokenProc(tokens)
//                 .then(result => {
//                   resolve(result)
//                 })
//                 .catch(error => {
//                   reject({ message: error.message })
//                 })
//             })
//             .catch(error => {
//               alert(error.message, () => {
//                 // 認証成功するまで再帰的に自分を実行
//                 recertification(tokens)
//                   .then(result => {
//                     resolve(result)
//                   })
//                   .catch(error => {
//                     reject({ message: error.message })
//                   })
//               })
//             })
//         },
//         () => {
//           confirm(
//             'サインアウトします。よろしいですか？',
//             () => {
//               void procAtSignout().then(() => {
//                 updateRefreshState(false, `サインアウトされました。(${Date.now()})`) // リフレッシュ時の待ち状態解除のため
//                 setLoadingView(false) // ここで処理中表示を解除しないとタイマ発火(最大5秒)まで処理中になってしまう。
//                 if (router.currentRoute.value.path !== '/') {
//                   void router.push({ path: '/' })
//                 } // 同じページに遷移させるとNavigation Errorになる。
//               })
//             },
//             () => {
//               // 再帰的に自分を実行
//               recertification(tokens)
//                 .then(result => {
//                   resolve(result)
//                 })
//                 .catch(error => {
//                   reject({ message: error.message })
//                 })
//             }
//           )
//         },
//         'large'
//       )
//     }
//   })
// }

// /**
//  * API-GWが提供する関数を実行する(POSTメソッド限定)。
//  * @param url WebAPIのURL
//  * @param path WebAPIのサブディレクトリ
//  * @param body リクエスト時に送信するメッセージボディ
//  * @param preCb 関数実行前に呼び出されるコールバック関数
//  * @param successCb 関数実行成功後に呼び出されるコールバック関数
//  * @param exceptionCb 例外受信時に呼び出されるコールバック関数
//  * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
//  * @param allwaysCb 処理終了後に必ず呼び出されるコールバック関数
//  * @param loadingView ローディング画面を表示するかどうか
//  */
// export const invokeApiGwFunc = (
//   url: string,
//   path: string,
//   body: object,
//   preCb: Function,
//   successCb: Function,
//   exceptionCb: Function,
//   unknownCb: Function,
//   allwaysCb: Function,
//   loadingView: boolean = true
// ): void => {
//   if (MiiaFwConfig.config.authorization.mustAuth) {
//     procUnderAuthed((tokens: TokenInfo) => {
//       invokeApiGwFuncWithToken(tokens, url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb)
//     }, loadingView)
//   } else {
//     invokeApiGwFuncWithoutToken(url, path, body, preCb, successCb, exceptionCb, unknownCb, allwaysCb)
//   }
// }

// // API Gatewayの認可対応のAPI実行処理本体
// /**
//  * invokeApiGwFuncWithTokenが呼び出す、API-GWが提供する認可処理を行った後API実行を行う処理本体。
//  * @param apigClient aws-api-gateway-clientパッケージによって生成されたクライアント
//  * @param pathParams GETメソッド使用時のパスリクエストパラメータ
//  * @param method HTTPメソッド
//  * @param additionalParams クエリが必要なときに入力するパラメータ
//  * @param url WebAPIのURL
//  * @param path WebAPIのサブディレクトリ
//  * @param body リクエスト時に送信するメッセージボディ
//  * @param preCb 関数実行前に呼び出されるコールバック関数
//  * @param successCb 関数実行成功後に呼び出されるコールバック関数
//  * @param exceptionCb 例外受信時に呼び出されるコールバック関数
//  * @param unknownCb 予期せぬエラー（アクセス回線障害）発生時に呼び出されるコールバック関数
//  * @param allwaysCb 処理終了後に必ず呼び出されるコールバック関数
//  * @param isRetryable 一度だけリトライするかどうか
//  */
// const invokeApiGwFuncWithAuthProc = (
//   apigClient: any,
//   pathParams: object,
//   method: string,
//   additionalParams: object,
//   url: string,
//   path: string,
//   body: object,
//   preCb: Function,
//   successCb: Function,
//   exceptionCb: Function,
//   unknownCb: Function,
//   allwaysCb: Function,
//   isRetryable: boolean
// ): void => {
//   apigClient
//     .invokeApi(pathParams, path, method, additionalParams, body)
//     .then((result: any) => {
//       if (Object.prototype.hasOwnProperty.call(result.data, 'error')) {
//         // Lambdaが例外をスローした場合
//         exceptionCb(result.data.error)
//       } else {
//         successCb(result.data)
//       }
//     })
//     .catch((error: any) => {
//       // API GWが自動生成するAPIはCredential Errorを検出できないので1回だけリトライする。
//       if (isRetryable) {
//         refreshToken()
//           .then(argTokens => {
//             invokeApiGwFuncWithToken(
//               argTokens as TokenInfo,
//               url,
//               path,
//               body,
//               preCb,
//               successCb,
//               exceptionCb,
//               unknownCb,
//               allwaysCb,
//               false
//             )
//           })
//           .catch(refreshError => {
//             // procAtSignout()は失敗しない。
//             // 【要検討】Refresh tokenを使用してAccess tokenをリフレッシュできなかった場合は、Top画面に戻るか再認証してリトライか?
//             void procAtSignout().then(() => {
//               alert(refreshError.message, () => {
//                 if (router.currentRoute.value.path !== '/') {
//                   void router.push({ path: '/' })
//                 } // 同じページに遷移させるとNavigation Errorになる。
//               })
//             })
//           })
//       } else {
//         console.error(error)
//         unknownCb()
//       }
//     })
//     .then(() => {
//       // then -> catch -> then = done -> fail -> always
//       setLastAccessDate(Date.now())
//       allwaysCb()
//     })
// }

/**
 * セッションストレージのセッション情報を消去する。
 */
export const clearTokens = (): void => {
  tempDataRemoveItem('access_token')
  tempDataRemoveItem('refresh_token')
  tempDataRemoveItem('id_token')
  tempDataRemoveItem('device_key')
  tempDataRemoveItem('auth_token')
  tempDataRemoveItem('token_secret')
  tempDataRemoveItem('provider')
  tempDataRemoveItem('session_id')
  tempDataRemoveItem('user_id') // ユーザIDも同じタイミングで消去
  tempDataRemoveItem('last_access') // 最後にWeb APIへアクセスした時間も同じタイミングで消去
  RemoveSessionInfo(['access_token', 'id_token', 'device_key'])
}

/**
 * セッションストレージへユーザIDを登録する（キー: user_id）。
 * @param userId ユーザーID
 */
export const setUserIdToStorage = (userId: string): void => {
  tempDataSetItem('user_id', userId)
}

/**
 * セッションストレージからユーザIDを取得する
 * @returns ユーザーID
 */
export const getUserIdToStorage = (): string | null => {
  return tempDataGetItem('user_id')
}

/**
 * セッションストレージへ最後にWeb APIへアクセスした時間を格納する(キー: last_access)。
 * @param lad Date.now()で取得される時間
 */
export const setLastAccessDate = (lad: number): void => {
  tempDataSetItem('last_access', String(lad))
}

// クレデンシャルを更新する。(S3の更新に必要)
/**
 * AWSのクレデンシャルを更新する。
 * @param tokens getTokensで取得できるセッション情報
 * @param userName ログインするユーザー
 * @throws エラーメッセージを持つErrorオブジェクトを返す。
 */
export const updateCredentials = (
  tokens: Partial<TokenInfo> & Pick<TokenInfo, 'provider'>,
  userName: string
): void => {
  let loginTo = ''
  const logins: { [any: string]: string } = {}
  const token = tokens.id_token
  if (tokens.provider.toLowerCase() === 'cognito' && token !== undefined) {
    const miiaFwUserPoolId =
      userName && getAuthSystemByUserId(userName) === AUTH_SYSTEM.DUP
        ? MiiaFwConfig.config.authorization.userPoolIdDup
        : MiiaFwConfig.config.authorization.userPoolId
    loginTo = `cognito-idp.${MiiaFwConfig.config.region}.amazonaws.com/${miiaFwUserPoolId}`
    logins[loginTo] = token // cognito: idトークン
  } else if (tokens.provider.toLowerCase() === 'google' && token !== undefined) {
    loginTo = 'accounts.google.com'
    logins[loginTo] = token // google: idトークン
  } else if (tokens.provider.toLowerCase() === 'facebook' && token !== undefined) {
    loginTo = 'graph.facebook.com'
    logins[loginTo] = token // facebook: accessトークン
  } else if (tokens.provider.toLowerCase() === 'twitter' && token !== undefined) {
    loginTo = 'api.twitter.com'

    logins[loginTo] = `${tokens.auth_token};${tokens.token_secret}` // twitter: Authトークンとトークンシークレットを「;」で連結したもの
  } else {
    throw new Error('対応していないIDプロバイダが設定されました。')
  }
  // config.credentialsを書き換えただけでは反映はされない(この時点では、config.credentials.needsRefresh == true)
  // config.credentials.refreshを実行すると、config.credentials.needsRefresh === falseとなる。
  // config.credentials.getを実行すると、未反映の場合は暗黙的にAWS.config.credentials.refreshが実行される。
  const identityPoolId =
    userName && getAuthSystemByUserId(userName) === AUTH_SYSTEM.DUP
      ? MiiaFwConfig.config.authorization.identityPoolIdDup
      : MiiaFwConfig.config.authorization.identityPoolId
  config.update({
    region: MiiaFwConfig.config.region,
    credentials: new CognitoIdentityCredentials(
      {
        IdentityPoolId: identityPoolId!,
        Logins: logins
      },
      {
        region: MiiaFwConfig.config.region
      }
      // 古い記述
      // {
      //   region: MiiaFwConfig.config.region,
      //   IdentityPoolId: MiiaFwConfig.config.authorization.identityPoolId,
      //   Logins: logins // ES2015(ES5)の記述要領(IE対応)
      //   // Logins: { [loginTo]: value }  // ES2015(ES6)の記述要領
      // }
    )
  })
}

// /**
//  * Facebookが提供するAPIを利用した認証処理を行う。
//  * Facebookのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はFacebookの認証ダイアログを開く。
//  * @param successCb 関数実行成功後に呼び出されるコールバック関数
//  * @param exceptionCb 例外受信時に呼び出されるコールバック関数
//  * @param closeCb 認証ダイアログを閉じた際に呼び出されるコールバック関数
//  */
// export const fbCheckStatus = (
//   successCb = () => {},
//   exceptionCb = (msg: any) => {
//     console.error(msg)
//   },
//   closeCb = () => {}
// ): void => {
//   // FB.getLoginStatus()は、アクセス先がHTTPSでないとログイン状態を取得できない。
//   window.FB.getLoginStatus((response: any) => {
//     if (response?.authResponse?.accessToken) {
//       // 他タブ、ウィンドウ、デバイスで既にログイン済の場合の処理
//       const tokens = {
//         provider: 'facebook',
//         access_token: response.authResponse.accessToken
//       }
//       setTokens(tokens)
//       updateIsAuthorized(true)
//       setUserIdToStorage(response.authResponse.userID)
//       setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
//       updateCredentials(tokens) // 【後で削除】S3でAWS SDKを使用するために必要
//       successCb()
//     } else if (
//       response?.authResponse ||
//       (response.error?.code &&
//         response.error.code === 190 &&
//         response.error.error_subcode &&
//         (response.error.error_subcode === 463 || response.error.error_subcode === 467))
//     ) {
//       // 未認証の場合、アクセストークン有効期限切れの場合(463)、自アカウントが他セッションによりサインアウトされた場合(467)
//       window.FB.login(
//         (res: any) => {
//           if (res?.authResponse?.accessToken) {
//             const tokens = {
//               provider: 'facebook',
//               access_token: res.authResponse.accessToken
//             }
//             setTokens(tokens)
//             updateIsAuthorized(true)
//             setUserIdToStorage(res.authResponse.userID)
//             setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
//             updateCredentials(tokens) // 【後で削除】S3でAWS SDKを使用するために必要
//             successCb()
//           } else {
//             if (!res.authResponse && res.status === 'unknown') {
//               // ユーザに認証ダイアログを閉じられた
//               closeCb()
//             } else if (res.error) {
//               // トークン切れとか自アカウントが他セッションによりサインアウトはないはず。

//               exceptionCb(`${res.error.type}(${res.error.message})`)
//             } else {
//               exceptionCb('API実行に失敗しました。(原因: アクセス回線障害など)')
//             }
//           }
//         },
//         { scope: 'email' }
//       )
//     } else if (response?.error) {
//       exceptionCb(`${response.error.type}(${response.error.message})`)
//     } else {
//       // その他のエラー
//       exceptionCb('API実行に失敗しました。(原因: アクセス回線障害など)')
//     }
//   })
// }

// /**
//  * Googleが提供するAPIを利用した認証処理を行う（作成途中）。
//  * Googleのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はGoogleの認証ダイアログを開く。
//  * @param successCb 関数実行成功後に呼び出されるコールバック関数
//  * @param exceptionCb 例外受信時に呼び出されるコールバック関数
//  * @param closeCb 認証ダイアログを閉じた際に呼び出されるコールバック関数
//  */
// export const googleCheckStatus = (
//   successCb = () => {},
//   exceptionCb = (error: { error: string; details: string }) => {
//     console.error(error)
//   },
//   closeCb = () => {}
// ): void => {
//   const siginInFunc = (internalSuccessCb: Function, internalExceptionCb: Function, internalCloseCb: Function): void => {
//     authInstance
//       .signIn()
//       .then(googleUser => {
//         // Useful data for your client-side scripts:
//         const profile = googleUser.getBasicProfile()
//         // The ID token you need to pass to your backend:
//         const authRes = googleUser.getAuthResponse()
//         const tokens = {
//           provider: 'google',
//           access_token: authRes.access_token,
//           id_token: authRes.id_token
//         }
//         setTokens(tokens)
//         updateIsAuthorized(true)
//         setUserIdToStorage(profile.getName())
//         setLastAccessDate(Date.now()) // サインイン後しばらくWeb APIをコールしない場合に備える。
//         updateCredentials(tokens) // 【後で削除】S3でAWS SDKを使用するために必要
//         internalSuccessCb()
//       })
//       .catch(error => {
//         if (error.error === 'popup_closed_by_user') {
//           internalCloseCb()
//         } else {
//           internalExceptionCb(error.error)
//         }
//       })
//   }
//   const authInstance = gapi.auth2.getAuthInstance()
//   authInstance.then(
//     () => {
//       // onInit
//       const isSignedIn = authInstance.isSignedIn.get()
//       if (isSignedIn) {
//         const googleUser = authInstance.currentUser.get()
//         const authRes = googleUser.getAuthResponse()
//         const tokens = {
//           provider: 'google',
//           access_token: authRes.access_token,
//           id_token: authRes.id_token
//         }
//         setTokens(tokens) // トークンはリフレッシュ後でもSession StrageやCookieに残るので不要
//         const profile = googleUser.getBasicProfile() // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
//         setUserIdToStorage(profile.getName()) // ユーザIDはリフレッシュ後でもSession Strageに残るので不要
//         updateCredentials(tokens) // リフレッシュ後に揮発するので要設定【後で削除】S3でAWS SDKを使用するために必要
//         updateIsAuthorized(true) // リフレッシュ後に揮発するので要設定
//         successCb()
//       } else {
//         // 未認証の場合、自アカウントが他セッションによりサインアウトされた場合
//         siginInFunc(successCb, exceptionCb, closeCb)
//       }
//     },
//     error => {
//       // onError
//       // TODO: トークン有効期限切れの場合の処理
//       exceptionCb(error)
//     }
//   )
// }

// /**
//  * Twitterが提供するAPIを利用した認証処理を行う（作成途中）。
//  * Twitterのログイン状況をチェックし、ログインが済んでいる場合はそのままセッション情報更新し、そうでない場合はTwitterの認証ダイアログを開く。
//  * @param _successCb 関数実行成功後に呼び出されるコールバック関数
//  * @param _exceptionCb 例外受信時に呼び出されるコールバック関数
//  * @param _closeCb 認証ダイアログを閉じた際に呼び出されるコールバック関数
//  */

// export const twitterCheckStatus = () =>
//   // _successCb = () => {},
//   // _exceptionCbt = (msg: string) => {
//   //   console.error(msg)
//   // },
//   // _closeCb = () => {}
//   {
//     alert('作成中です！')
//   }
